import _ from "lodash";
import React, { useState, useRef, useEffect } from "react";
import useI18n from "Hooks/UseI18n";
import { IconButton, Tabs } from "@toddle-edu/ds-web";
import {
  InformationOutlined,
  CloseOutlined,
  Camera3D,
  Record3D,
  Video3D,
  Files3D,
  Notes3D,
  Link3D,
  Workbook3D,
  PhotoCollage3D,
} from "@toddle-edu/ds-icons";
import { SeniorFolioDisplaySvg } from "SvgComponents";
import { Popover, ArrowContainer } from "@toddle-edu/react-tiny-popover";
import { colors } from "Constants";
import classes from "./PortfolioInfoTooltip.scss";
import { generateRandomId } from "Utils";
const getArrowDegree = position => {
  switch (position) {
    case "top":
      return 225;
    case "right":
      return 315;
    case "bottom":
      return 45;
    case "left":
      return 135;
    default:
      return 45;
  }
};

const styles = {
  arrowStyle: ({ position }) => ({
    backgroundColor: colors.surfaceWhite,
    width: "20px",
    height: "20px",
    transform: `rotate(${getArrowDegree(position)}deg)`,
    borderLeft: `1px solid ${colors.borderSubtle}`,
    borderTop: `1px solid ${colors.borderSubtle}`,
  }),
  overlayInnerStyle: {
    maxWidth: "240px",
    textAlign: "center",
  },
};

const PortfolioInfoTooltip = props => {
  const { containerRef, id } = props;
  const [isVisible, setIsVisible] = useState(false);
  const { t } = useI18n(["common", "journal"]);
  const randomId = generateRandomId();
  const iconButtonRef = useRef(null);
  const crossButtonRef = useRef(null);
  const clickMeButtonRef = useRef(null);
  const referenceId = !_.isNil(id) ? id : randomId;
  const showPopover = () => setIsVisible(true);
  const hidePopover = () => {
    const mediaTypeSubtext = document.querySelector(
      `#popover-trigger-button-${referenceId}`
    );
    mediaTypeSubtext?.focus();
    setIsVisible(false);
  };

  useEffect(() => {
    if (isVisible && crossButtonRef.current) {
      crossButtonRef.current?.focus();
    }
  }, [crossButtonRef, isVisible]);

  const portfolioTypeTabs = [
    { value: "junior", label: t("journal:junior_portfolio") },
    { value: "senior", label: t("journal:senior_portfolio") },
  ];
  const [activeTab, setActiveTab] = useState("junior");
  const onToggleActiveTab = value => {
    setActiveTab(value);
  };
  const getPortfolioDescription = activeTab => {
    return activeTab === "junior"
      ? t("journal:junior_folio_description")
      : t("journal:senior_folio_description");
  };

  const addMediaOption = [
    {
      type: "PHOTO",
      title: "common:photo",
      color: colors.violet900,
    },
    {
      type: "VOICE",
      title: "common:voice",
      color: "#efdef8",
    },
    {
      type: "VIDEO",
      title: "common:video",
      color: "#ffdce5",
    },
    {
      type: "FILE",
      title: "common:file",
      color: "#bdf3e6",
    },
    {
      type: "NOTE",
      title: "common:note",
      color: "#ffeabf",
    },
    {
      type: "LINK",
      title: "common:link",
      color: "#c5f6ff",
    },
    {
      type: "WORKBOOK",
      title: "common:workbook",
      color: "#fee2d0",
    },
    {
      type: "PHOTO_COLLAGE",
      title: "journal:photo_collage",
      color: "#E4F6BC",
    },
  ];

  const getAttachmentIcon = type => {
    switch (type) {
      case "PHOTO":
        return <Camera3D height={84} width={84} />;
      case "VOICE":
        return <Record3D height={84} width={84} />;
      case "VIDEO":
        return <Video3D height={84} width={84} />;
      case "FILE":
        return <Files3D height={84} width={84} />;
      case "NOTE":
        return <Notes3D height={84} width={84} />;
      case "LINK":
        return <Link3D height={84} width={84} />;
      case "WORKBOOK":
        return <Workbook3D height={84} width={84} />;
      case "PHOTO_COLLAGE":
        return <PhotoCollage3D height={84} width={84} />;
    }
  };
  return (
    <Popover
      isOpen={isVisible}
      positions={["bottom"]}
      align={"center"}
      arrowColor={colors.white}
      onClickOutside={hidePopover}
      containerClassName={classes.popupContainerClassName}
      ref={clickMeButtonRef}
      arrowClassName={classes.arrow}
      containerParent={containerRef?.current}
      content={({ position, childRect, popoverRect }) =>
        position ? (
          <ArrowContainer
            position={position}
            childRect={childRect}
            popoverRect={popoverRect}
            arrowSize={10}
            arrowStyle={styles.arrowStyle({
              position,
            })}
          >
            <div className={classes.overlayContainer}>
              <div className={classes.infoTooltipHeader}>
                <span>{t("journal:portfolio_type")}</span>
                <IconButton
                  tabIndex={0}
                  ref={crossButtonRef}
                  icon={<CloseOutlined />}
                  variant="outlined"
                  type="plain"
                  size="x-small"
                  onClick={hidePopover}
                />
              </div>
              <div style={{ borderBottom: `1px solid ${colors.strokeOne}` }}>
                <Tabs
                  options={portfolioTypeTabs}
                  value={activeTab}
                  onChange={onToggleActiveTab}
                  type={"fixed"}
                  containerStyle={{ width: "100%" }}
                />
              </div>
              <div className={classes.description}>
                {getPortfolioDescription(activeTab)}
              </div>
              {activeTab === "junior" ? (
                <div className={classes.container}>
                  {_.map(addMediaOption, item => {
                    return (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                        }}
                      >
                        <div
                          className={classes.iconContainer}
                          style={{ backgroundColor: item.color }}
                        >
                          {getAttachmentIcon(item.type)}
                        </div>
                        <div className={classes.iconText}>{t(item.title)}</div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ paddingRight: "16px" }}>
                  <SeniorFolioDisplaySvg />
                </div>
              )}
            </div>
          </ArrowContainer>
        ) : null
      }
    >
      <IconButton
        id={`popover-trigger-button-${referenceId}`}
        icon={<InformationOutlined />}
        variant="outlined"
        type="plain"
        size="x-small"
        ref={iconButtonRef}
        onClick={showPopover}
      />
    </Popover>
  );
};

export default PortfolioInfoTooltip;
